<template>
	<div class="ido">
		<div class="ido__elm">
			<!-- //////////////////// -->
			<!-- HEADER -->
			<div @click="onClickShowOptions" class="ido__elm-header">
				<div class="ido__elm-header-text">Покупка опций</div>
				<div
					:class="
						isShowOptions
							? 'ido__elm-header-icon-down'
							: 'ido__elm-header-icon-up'
					"
				>
					<IconChevronDown />
				</div>
			</div>

			<!-- //////////////////// -->
			<!-- BODY -->
			<div class="ido__elm-body" v-if="isShowOptions">
				<!-- //////////////////// -->
				<!-- OPTIONS LIST -->
				<div v-for="(item, index) in optionList" :key="index">
					<div v-if="excludePaid(item.id)" class="ido__elm-body-item">
						<div class="ido__elm-body-item-checkbox">
							<UiSwitcher
								:is-array-items="true"
								:name="`${item.name}`"
								:item-id="Number(item.id)"
								v-on:checked-item="checked"
							/>
						</div>
						<div class="ido__elm-body-item-text">
							{{ item.name }}
						</div>

						<div class="ido__elm-body-item-text">
							{{ item.cost }} руб.
						</div>
					</div>
				</div>

				<!-- Разделительная линия -->
				<div
					v-if="optionsMap.size !== 0"
					class="ido__elm-body-line"
				></div>

				<!-- /////////////////////////// -->
				<!-- Информация для пользователя -->
				<div
					v-if="!isPayMonthDisable && isPayMonthChecked"
					class="ido__elm-body-expired"
				>
					<div class="ido__elm-body-expired-info">
						<div class="ido__elm-body-expired-info-item">
							Канал будет продлён до: {{ payInstanceExpired }}.
						</div>

						<div class="ido__elm-body-expired-info-item">
							Расчётный период сместится на:
							{{ billingPeriod }} дн.
						</div>

						<div class="ido__elm-body-expired-info-item">
							Доплата за канал составит:
							{{ Math.round(surcharge) }} руб.
						</div>

						<div
							v-if="isPaidOptionsNeedRenew"
							class="ido__elm-body-expired-info-item_option"
						>
							Доплата за опции:
						</div>

						<!-- ~~~~~~~~~~~~~~~~~~~~ -->
						<!-- Информация по опциям -->
						<div
							v-for="(item, index) in surchargeOptionList"
							:key="index"
						>
							<div class="ido__elm-body-expired-info-item_option">
								{{ index + 1 }}. {{ item.name }}: составит
								{{ Math.round(item.cost) }} руб.
							</div>
						</div>
					</div>
				</div>

				<!-- //////////////////// -->
				<!-- Чекбокс для покупки опци на месяц -->
				<div v-if="!isPayMonthDisable" class="ido__elm-body-item">
					<div class="ido__elm-body-item-checkbox">
						<UiSwitcher
							:is-array-items="true"
							name="pay_for_month"
							:item-id="1"
							v-on:checked-item="payMonthChecked"
							:is-disable="isPayMonthDisable"
							:is-checked="isPayMonthChecked"
						/>
					</div>

					<div class="ido__elm-body-item-text">
						Хочу продлить на месяц.
					</div>
				</div>
			</div>

			<!-- //////////////////// -->
			<!-- FOOTER -->
			<div class="ido__elm-footer" v-if="optionsMap.size !== 0">
				<UiButton
					@click="onClickPay"
					:label="`Оплатить ${payBtnInfo} руб`"
					background-color="var(--theme-color)"
					border-color="var(--theme-color)"
					color="rgb(255, 255, 255)"
				/>
				<div v-show="false">
					{{ paidOptionsCalculate }}
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import IconChevron from "@/components/icons/IconChevron.vue";
import IconChevronDown from "@/components/icons/IconChevronDown.vue";
import UiSwitcher from "@/components/ui/UiSwitcher.vue";
import UiButton from "@/components/ui/UiButton.vue";
import { mapActions, mapGetters } from "vuex";
import { IOptions } from "@/interfaces/IOptions";
import { IPaidOptions } from "@/interfaces/IPaidOptions";
import { IInstanceDetail } from "@/interfaces/IInstanceDetail";
import dayjs from "dayjs";

@Options({
	components: {
		UiButton,
		UiSwitcher,
		IconChevronDown,
		IconChevron,
	},
	props: {
		paidOptions: { type: Array, default: [] },
	},

	computed: {
		...mapGetters("instanceModule", {
			optionList: "optionListGetters",
			instance: "getInstance",
		}),

		...mapGetters("paymentModule", {
			payOptions: "payOptionsGetters",
			payChannelDetail: "payChannelDetailGetters",
		}),
	},

	methods: {
		...mapActions("paymentModule", ["payOptionsAction"]),
	},
})
export default class InstanceDetailOptions extends Vue {
	public paidOptions: Array<IPaidOptions>;

	public optionsMap = new Map();
	public options: Array<number> = [];
	public isPayMonthDisable = true;
	public isPayMonthChecked = false;
	public payInstanceExpired = "";
	public billingPeriod = 0;
	public surcharge = 0;
	public payBtnInfo = 0;
	public sumOptions = 0;

	public surchargeOptionList: Array<{ name: string; cost: number }>;

	// ************************************************************
	// Define mapGetters
	public optionList: Array<IOptions>;
	public payOptions: { createdId: number; url: string };
	public instance: IInstanceDetail;
	public payChannelDetail: {
		tariff: {
			userId: number;
			name: string;
			cost: number;
			type: number;
			partnerId: number;
		};
	};

	// ************************************************************
	// Define mapActions

	public payOptionsAction!: (payload: {
		optionsIds: Array<number>;
		instanceId: number;
		isPayMonth: boolean;
	}) => Promise<void>;

	public isShowOptions = false;

	/**
	 * Показать список опций
	 */
	public async onClickShowOptions() {
		this.isShowOptions = !this.isShowOptions;
		this.isPayMonthChecked = false;
		this.isPayMonthDisable = true;
		this.optionsMap.clear();
	}

	/**
	 * Возвращает true, если имеются купленные опции с автопродлением
	 */
	public get isPaidOptionsNeedRenew(): boolean {
		for (let idx = 0; idx < this.paidOptions.length; idx++) {
			if (this.paidOptions[idx].need_renew === 1) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Обработка выбранных опций
	 * @param val
	 */
	public checked(val: { id: number; name: string; isActive: boolean }) {
		if (val.isActive) {
			this.optionsMap.set(val.id, val.id);
		} else {
			this.optionsMap.delete(val.id);
		}

		// Количество отмеченных чекбоксов (Покупка опций)
		if (this.optionsMap.size === 0) {
			this.sumOptions = 0;
		}

		this.options = [];

		let sum = 0;
		this.optionsMap.forEach((item) => {
			const opt = this.getOptionById(item) as IOptions;
			sum += opt.cost;
		});

		// Если опция не выбрана, то checkbox (Хочу продлить на месяц) скрываем
		this.isPayMonthDisable = this.optionsMap.size === 0;

		if (this.isPayMonthDisable) {
			this.surcharge = 0;
			this.isPayMonthChecked = false;
		}

		this.payBtnInfo = sum + Math.round(this.surcharge) + this.sumOptions;
	}

	/**
	 * Обработка события (checkbox - автопродление)
	 */
	public get paidOptionsCalculate() {
		this.calculate();
		return this.paidOptions;
	}

	/**
	 * Доплата за опции
	 */
	public calculateForOptions(expired: number, DAYS_IN_MONTH = 31) {
		const surchargeOptionArr: Array<{ name: string; cost: number }> = [];

		for (let idx = 0; idx < this.paidOptions.length; idx++) {
			if (this.paidOptions[idx].need_renew === 1) {
				const optionId = this.paidOptions[idx].option_id;
				const option = this.getOptionById(optionId) as IOptions;

				// Вычитаем разницу, и получаем количество дней насколько будет продление опции
				const days = expired - this.paidOptions[idx].expire_stamp;
				const billingPeriod = Number(dayjs(days).format("D")) - 1;

				// Расчёт стоимости доплаты за опцию
				const surchargeOption =
					(option.cost / DAYS_IN_MONTH) * billingPeriod;

				surchargeOptionArr.push({
					name: option.name,
					cost: surchargeOption,
				});

				this.sumOptions += Math.round(surchargeOption);
			}
		}

		this.surchargeOptionList = surchargeOptionArr;

		return this.sumOptions;
	}

	/**
	 * Пересчёт, если меняем состояние чекбокса - автопродление
	 */
	public calculate() {
		// Дата на месяц вперёд
		const expired = dayjs().add(1, "month").unix() * 1000;
		this.payInstanceExpired = dayjs(expired).format("DD MMMM HH:mm");

		// Вычитаем разницу, и получаем количество дней насколько будет продление канала
		const days = expired - this.instance.expire_payment_stamp;
		this.billingPeriod = Number(dayjs(days).format("D")) - 1;

		const DAYS_IN_MONTH = dayjs(new Date()).daysInMonth();

		// Расчёт стоимости доплаты за канал
		this.surcharge =
			(this.payChannelDetail.tariff.cost / DAYS_IN_MONTH) *
			this.billingPeriod;

		this.options = [];

		let sum = 0;
		this.optionsMap.forEach((item) => {
			const opt = this.getOptionById(item) as IOptions;
			sum += opt.cost;
		});

		if (this.isPayMonthChecked) {
			this.sumOptions = 0;

			// Также нужно учитывать купленные опции, если у них установлен флаг автопродления
			// Получаем данные купленных опций с флагом 1 (true)
			this.sumOptions = this.calculateForOptions(expired, DAYS_IN_MONTH);

			this.payBtnInfo =
				sum + Math.round(this.surcharge) + this.sumOptions;

			return this.payBtnInfo;
		}

		this.payBtnInfo = sum;

		return this.payBtnInfo;
	}

	/**
	 * Обработка чекбокса для оплаты на месяц
	 * @param val
	 */
	public payMonthChecked(val: {
		id: number;
		name: string;
		isActive: boolean;
	}) {
		this.isPayMonthChecked = val.isActive;
		this.calculate();
	}

	/**
	 * Возвращает информацию для опции по id
	 * @param optionId
	 */
	public getOptionById(optionId: number): IOptions | string {
		for (let idx = 0; idx < this.optionList.length; idx++) {
			if (this.optionList[idx].id === optionId) {
				return this.optionList[idx];
			}
		}

		return "";
	}

	/**
	 * Исключаем уже купленные опции
	 * @param optionId
	 */
	public excludePaid(optionId: number) {
		for (let idx = 0; idx < this.paidOptions.length; idx++) {
			if (this.paidOptions[idx].option_id === optionId) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Обработка кнопки - Оплатить
	 */
	public async onClickPay() {
		const instanceId = Number(this.$route.params.id);
		this.options = [];

		this.optionsMap.forEach((item) => {
			this.options.push(item);
		});

		await this.payOptionsAction({
			optionsIds: this.options,
			instanceId: instanceId,
			isPayMonth: this.isPayMonthChecked, // С продлением на месяц
		});

		if (this.payOptions.url !== "") {
			window.location.href = this.payOptions.url;
		}
	}
}
</script>

<style scoped lang="scss">
.ido {
	display: flex;
	justify-content: center;

	&__elm {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		max-width: 649px;
		background: rgb(255, 255, 255);
		border-radius: 12px;
		box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.02);
	}

	&__elm-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 24px;
		user-select: none;
		color: rgb(49, 49, 50);
		font-size: 20px;
		font-weight: 600;
		cursor: pointer;
	}

	&__elm-header-icon-up {
		transform: rotate(0deg);
	}

	&__elm-header-icon-down {
		transform: rotate(180deg);
	}

	&__elm-body {
		display: flex;
		flex-direction: column;
		padding: 12px 24px 0;
	}

	&__elm-body-line {
		padding: 12px;
		border-top: 1px solid rgb(223, 230, 238);
	}

	&__elm-body-item {
		display: grid;
		grid-template-columns: 35px 0.5fr 1fr;
		margin-bottom: 16px;
	}

	&__elm-body-expired {
		display: flex;
		padding: 0 0 24px;
	}

	&__elm-body-item-text {
		color: rgb(49, 49, 50);
	}

	&__elm-footer {
		display: flex;
		padding: 0 24px 24px;
		justify-content: flex-end;
	}

	&__elm-body-expired-info-item {
		padding-top: 12px;
	}

	&__elm-body-expired-info-item_option {
		padding-top: 12px;
		color: #4d4c4c;
	}

	@media screen and (max-width: 768px) {
		&__elm-header {
			padding: 16px;
		}

		&__elm-body {
			padding: 0 16px 16px;
		}

		&__elm-footer {
			padding: 0 12px 12px;
		}
	}
}
</style>
